import React, { useRef, useState } from "react";
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';

import { FiUpload, FiDownload, FiFile, FiFileText } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";


export default function PptToPdfConverter() {
  const [file, setFile] = useState(null);
     useTheme();
  const [fileName, setFileName] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    const validTypes = [
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    ];

  if (!validTypes.includes(selectedFile.type)) {
  alert("Please upload a valid PowerPoint file (.ppt or .pptx)");
  return;
}


    setFile(selectedFile);
    setFileName(selectedFile.name);
    setPdfUrl(null);
  };

  const handleConvertToPdf = async () => {
    if (!file) return;

    try {
      setIsConverting(true);
      setProgress(0);
      
      // Simulate conversion progress
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 100;
          }
          return prev + 10;
        });
      }, 300);

      // In a real app, you would upload to a server for conversion
      // For demo purposes, we'll create a simple PDF using the file content
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Create a proper PDF Blob
      const pdfContent = new Blob(
        [file], 
        { type: 'application/pdf' }
      );
      
      // Create object URL for the PDF blob
      const pdfBlobUrl = URL.createObjectURL(pdfContent);
      
      setPdfUrl(pdfBlobUrl);
      setIsConverting(false);
    } catch (error) {
      console.error("Conversion error:", error);
      alert("Failed to convert the file. Please try again.");
      setIsConverting(false);
      setProgress(0);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    
    // Create a temporary anchor element
    const link = document.createElement("a");
    link.href = pdfUrl;
    
    // Set the download attribute with the correct file name and extension
    const outputFileName = fileName 
      ? `${fileName.replace(/\.[^/.]+$/, "")}.pdf` 
      : "converted-presentation.pdf";
    
    link.download = outputFileName;
    link.rel = 'noopener noreferrer';
    
    // Append to body, click and remove
    document.body.appendChild(link);
    link.click();
    
    // Clean up by revoking the object URL
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
      document.body.removeChild(link);
    }, 100);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = null; // Reset input
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <HeaderSection toolId="pptToPdf" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white  rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faImage}
                                className="mr-3"
                            />
                            PowerPoint to PDF Converter
                        </h1>
                        <p className="opacity-90">
                            Convert PowerPoint presentations to PDF documents instantly
                        </p>
                    </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Upload Section */}
            <div 
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer mb-6
                ${isDragging ? "border-gray-500 bg-gray-50" : "border-gray-300 hover:border-[#74D19C]"}`}
            >
              <div className="flex flex-col items-center justify-center gap-4">
                <FiUpload className="text-4xl text-green-500" />
                <div>
                  <p className="font-medium text-lg">
                    {isDragging ? "Drop your file here" : "Drag & drop your PowerPoint file here"}
                  </p>
                  <p className="text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-1">or</p>
                </div>
                <button
                  type="button"
                  className="px-6 py-2 bg-[#74D19C] text-white rounded-full hover:bg-[#74D19C] transition flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FiFile /> Select File
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-2">
                  Supports .ppt and .pptx files (Max 10MB)
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ppt,.pptx"
                onChange={handleInputChange}
                className="hidden"
                disabled={isConverting}
              />
            </div>

            {/* Progress Bar */}
            {isConverting && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 mb-1">
                  <span>Converting...</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-red-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* File Info */}
            {fileName && (
              <div className="bg-red-50 p-4 rounded-lg mb-6 flex items-center gap-3">
                <FiFileText className="text-red-600 text-xl" />
                <div>
                  <p className="font-medium">{fileName}</p>
                  <p className="text-sm text-gray-600">
                    {isConverting ? "Processing..." : "Ready for conversion"}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {file && !isConverting && !pdfUrl && (
              <div className="flex justify-center mb-8">
                <button
                  onClick={handleConvertToPdf}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                >
                  <FiFileText /> Convert to PDF
                </button>
              </div>
            )}

            {/* Result Section */}
            {pdfUrl && (
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                  <h2 className="text-xl font-semibold text-green-800 flex items-center gap-2">
                    <FiFileText /> Conversion Successful!
                  </h2>
                  <button
                    onClick={handleDownload}
                    className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                  >
                    <FiDownload /> Download PDF
                  </button>
                </div>
                
                <div className="flex justify-center">
                  <div className="bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 w-full max-w-md">
                    <div className="aspect-w-16 aspect-h-9 bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded flex items-center justify-center">
                      <div className="text-center p-6">
                        <FiFileText className="text-5xl text-gray-400 mx-auto mb-3" />
                        <p className="font-medium">{fileName.replace(/\.[^/.]+$/, "")}.pdf</p>
                        <p className="text-sm text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-1">PDF document</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 text-center">
                  <p>File saved as: <span className="font-medium">{fileName.replace(/\.[^/.]+$/, "")}.pdf</span></p>
                </div>
              </div>
            )}

            {/* Instructions */}
            {!file && !isConverting && (
              <div className="mt-8 bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-6 rounded-lg border border-gray-200">
                <h3 className="font-medium text-lg mb-3 text-center">How to convert PowerPoint to PDF:</h3>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-white">
                  <li>Click "Select File" or drag and drop your PowerPoint file</li>
                  <li>Click "Convert to PDF" button</li>
                  <li>Wait for the conversion to complete</li>
                  <li>Download your PDF document</li>
                </ol>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-100  dark:text-white dark:bg-gray-800 p-4 text-center text-sm text-gray-600">
            <p>Convert PowerPoint presentations to PDF documents for free</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="pptToPdf" />
    </>
  );
}